import { useMemo } from 'react';

import { Card } from '@/components/ui/card';
import { PopoverContent } from '@/components/ui/popover';
import { PortalBootResponse } from '@/lib/types';
import { useAppState } from '@/state/hooks/app.state.hook';
import styled, { css } from 'styled-components';

import {
  cssObjectToString,
  parseCssObject,
  scopeSelfOrDescendant,
  selfOrDescendant,
  type CssObject,
} from './cssObjectToString';

export const DEFAULT_FOOTER_BACKGROUND_COLOR = '#fff';

type FooterTheme = PortalBootResponse['theme']['footer'] | undefined;

interface FooterScopeProps {
  $footer: FooterTheme;
  $customCss?: CssObject;
  $isTransparent?: boolean;
}

// Unset theme colours must emit nothing, so the component's own defaults still apply.
const declare = (property: string, value?: string): string =>
  value ? `${property}: ${value};` : '';

const footerScopeStyles = css<FooterScopeProps>`
  ${selfOrDescendant('.footer-container')} {
    background: ${({ $footer, $isTransparent }) =>
      $isTransparent
        ? 'transparent'
        : $footer?.backgroundColor?.colorOne || DEFAULT_FOOTER_BACKGROUND_COLOR};
    ${({ $footer }) => declare('color', $footer?.textColor)}
  }

  ${selfOrDescendant('.footer-language-picker')},
  ${selfOrDescendant('.footer-language-menu')} {
    ${({ $footer }) => declare('color', $footer?.textColor)}
    ${({ $footer }) => declare('background-color', $footer?.backgroundColor?.colorOne)}
  }

  ${({ $customCss }) => cssObjectToString(scopeSelfOrDescendant($customCss))}
`;

export const StyledFooter = styled(Card)<FooterScopeProps>`
  ${footerScopeStyles}
`;

export const StyledFooterLanguageMenu = styled(PopoverContent)<FooterScopeProps>`
  ${footerScopeStyles}
`;

export const useFooterScopeProps = (): {
  $footer: FooterTheme;
  $customCss: CssObject | undefined;
} => {
  const { initialBoot } = useAppState();
  const footer = initialBoot?.theme?.footer;

  return {
    $footer: footer,
    $customCss: useMemo(() => parseCssObject(footer?.css), [footer?.css]),
  };
};
